import { useMemo, memo } from 'react'
import { useTranslation } from '@/i18n'
import { Challenge } from '@/types/challenge'
import { cn } from '@/utils/cn'

interface ChallengeStatisticsProps {
  challenge: Challenge
  className?: string
}

interface StatItem {
  label: string
  value: string | number
  trend?: 'up' | 'down' | 'neutral'
  icon: React.ReactNode
  color: string
}

const ChallengeStatistics = memo(function ChallengeStatistics({ challenge, className }: ChallengeStatisticsProps) {
  const { t } = useTranslation()

  const stats = useMemo(() => {
    const validatedDays = challenge.validatedDays || []
    const totalDays = Math.max(1, Math.floor((Date.now() - new Date(challenge.createdAt).getTime()) / (1000 * 60 * 60 * 24)) + 1)
    const completionRate = Math.round((validatedDays.length / totalDays) * 100)
    
    // Calculate weekly completion rate (last 7 days)
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - i)
      return date.toISOString().split('T')[0]
    })
    
    const completedLast7Days = last7Days.filter(day => validatedDays.includes(day)).length
    const weeklyRate = Math.round((completedLast7Days / 7) * 100)
    
    // Calculate average check-ins per day
    const avgCheckInsPerDay = totalDays > 0 ? (challenge.totalCheckIns / totalDays).toFixed(1) : '0.0'
    
    // Calculate days since last check-in
    const lastCheckInDate = challenge.lastCheckIn ? new Date(challenge.lastCheckIn) : null
    const daysSinceLastCheckIn = lastCheckInDate 
      ? Math.floor((Date.now() - lastCheckInDate.getTime()) / (1000 * 60 * 60 * 24))
      : totalDays

    const statItems: StatItem[] = [
      {
        label: t('statistics.completionRate'),
        value: `${completionRate}%`,
        trend: completionRate >= 80 ? 'up' : completionRate >= 60 ? 'neutral' : 'down',
        color: completionRate >= 80 ? 'text-green-600' : completionRate >= 60 ? 'text-yellow-600' : 'text-red-600',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        )
      },
      {
        label: t('statistics.weeklyCompletion'),
        value: `${weeklyRate}%`,
        trend: weeklyRate >= completionRate ? 'up' : 'down',
        color: weeklyRate >= 80 ? 'text-green-600' : weeklyRate >= 60 ? 'text-yellow-600' : 'text-red-600',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      },
      {
        label: t('statistics.totalCheckIns'),
        value: challenge.totalCheckIns || 0,
        color: 'text-blue-600',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      },
      {
        label: t('statistics.avgPerDay'),
        value: avgCheckInsPerDay,
        color: 'text-gray-600',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
        )
      },
      {
        label: t('statistics.currentStreak'),
        value: `${challenge.currentStreak || 0} ${t('common.days')}`,
        color: 'text-orange-600',
        icon: (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" />
          </svg>
        )
      },
      {
        label: t('statistics.daysSinceLastCheckIn'),
        value: daysSinceLastCheckIn === 0 ? t('statistics.today') : `${daysSinceLastCheckIn} ${t('common.days')}`,
        trend: daysSinceLastCheckIn === 0 ? 'up' : daysSinceLastCheckIn === 1 ? 'neutral' : 'down',
        color: daysSinceLastCheckIn === 0 ? 'text-green-600' : daysSinceLastCheckIn === 1 ? 'text-yellow-600' : 'text-red-600',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      }
    ]

    return statItems
  }, [challenge, t])

  return (
    <div className={cn('bg-surface rounded-xl border border-border shadow-sm p-xl', className)}>
      <h3 className="text-xl font-semibold text-text mb-lg">{t('statistics.challengeInsights')}</h3>
      
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-lg">
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className={cn('inline-flex items-center justify-center w-12 h-12 rounded-full mb-3', 
              stat.color.replace('text-', 'bg-').replace('-600', '-100')
            )}>
              <div className={stat.color}>
                {stat.icon}
              </div>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1">
                <span className={cn('text-2xl font-bold', stat.color)}>
                  {stat.value}
                </span>
                {stat.trend && (
                  <div className={cn('w-4 h-4', 
                    stat.trend === 'up' ? 'text-green-500' : 
                    stat.trend === 'down' ? 'text-red-500' : 'text-gray-400'
                  )}>
                    {stat.trend === 'up' && (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                      </svg>
                    )}
                    {stat.trend === 'down' && (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    )}
                    {stat.trend === 'neutral' && (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              <p className="text-sm text-text-muted font-medium">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
})

// Simple progress chart component
const ProgressChart = memo(function ProgressChart({ challenge }: { challenge: Challenge }) {
  const { t } = useTranslation()
  
  const last30Days = useMemo(() => {
    const days = []
    for (let i = 29; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toISOString().split('T')[0]
      const isCompleted = (challenge.validatedDays || []).includes(dateStr)
      days.push({ date: dateStr, completed: isCompleted })
    }
    return days
  }, [challenge])
  
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-xl">
      <h3 className="text-xl font-semibold text-text mb-lg">{t('statistics.last30Days')}</h3>
      
      <div className="grid grid-cols-10 gap-1">
        {last30Days.map((day, index) => (
          <div
            key={index}
            className={cn(
              'aspect-square rounded-sm',
              day.completed ? 'bg-success' : 'bg-bg-alt'
            )}
            title={new Date(day.date).toLocaleDateString()}
          />
        ))}
      </div>
      
      <div className="mt-4 flex items-center justify-between text-sm text-text-muted">
        <span>{t('statistics.less')}</span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4].map(level => (
            <div
              key={level}
              className={cn(
                'w-3 h-3 rounded-sm',
                level === 1 ? 'bg-bg-alt' :
                level === 2 ? 'bg-success/30' :
                level === 3 ? 'bg-success/60' : 'bg-success'
              )}
            />
          ))}
        </div>
        <span>{t('statistics.more')}</span>
      </div>
    </div>
  )
})

export { ChallengeStatistics, ProgressChart }